import { getTemplateById, selectRhythmTemplate } from './curriculumRhythmTemplates';
import { inferRoutineSlotCategories } from './routineSlotCategories';

function toActivityRef(activity) {
  return {
    kbItemId: activity.kbItemId,
    title: activity.title,
    description: activity.description,
    duration: activity.recommendedFrequency?.sessionMinutes || 15,
    score: activity.score,
    targetDomains: activity.targetDomains,
    frequency: activity.recommendedFrequency,
  };
}

function activityCategories(activity) {
  return inferRoutineSlotCategories({
    title: activity.title,
    tags: activity.tags,
    targetDomains: activity.targetDomains,
    routineSlotCategories: activity.routineSlotCategories,
  });
}

function slotAccepts(activity, accepts) {
  const cats = activityCategories(activity);
  return cats.some((c) => accepts.includes(c));
}

function pickBestForSlot(activities, accepts, usedIds) {
  const candidates = activities
    .filter((a) => slotAccepts(a, accepts) && !usedIds.has(a.kbItemId))
    .sort((a, b) => (b.score || 0) - (a.score || 0));
  if (candidates.length === 0) {
    const fallback = activities
      .filter((a) => !usedIds.has(a.kbItemId))
      .sort((a, b) => (b.score || 0) - (a.score || 0));
    return fallback[0] || null;
  }
  return candidates[0];
}

export function fillRoutineTimetable(activities, template, studentId, termId) {
  const structured = activities.filter((a) => a.kbItemId);
  const usedIds = new Set();
  const anchors = {};
  const variables = {};

  const anchorDefs = template.slots.filter((s) => s.kind === 'anchor').sort((a, b) => a.order - b.order);
  const variableDefs = template.slots.filter((s) => s.kind === 'variable').sort((a, b) => a.order - b.order);

  for (const slot of anchorDefs) {
    const picked = pickBestForSlot(structured, slot.acceptsCategories, usedIds);
    const refs = picked ? (usedIds.add(picked.kbItemId), [toActivityRef(picked)]) : [];
    anchors[slot.id] = {
      slotId: slot.id,
      label: slot.label,
      kind: 'anchor',
      activities: refs,
    };
  }

  for (const slot of variableDefs) {
    const days = slot.defaultDays || [];
    const byDay = {};
    for (const day of days) {
      const picked = pickBestForSlot(structured, slot.acceptsCategories, usedIds);
      if (picked) {
        usedIds.add(picked.kbItemId);
        byDay[day] = [toActivityRef(picked)];
      } else {
        byDay[day] = [];
      }
    }
    variables[slot.id] = {
      slotId: slot.id,
      label: slot.label,
      kind: 'variable',
      activities: [],
      scheduledDays: [...days],
      byDay,
    };
  }

  return {
    schemaVersion: 2,
    templateId: template.id,
    templateLabel: template.label,
    routingPath: 'below_nc_routine',
    studentId,
    termId,
    anchors,
    variables,
  };
}

export function buildRoutineTimetableForStudent(
  activities,
  ncStatus,
  questionnaireAnswers,
  ageYears,
  studentId,
  termId,
  templateId
) {
  const template =
    (templateId && getTemplateById(templateId)) ||
    selectRhythmTemplate(ncStatus, questionnaireAnswers, ageYears);
  return fillRoutineTimetable(activities, template, studentId, termId);
}

export function isRoutineTimetable(doc) {
  return doc && doc.schemaVersion === 2 && doc.anchors;
}
